import React from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@tempmee/react';
import { TableCardFrame } from '../../../commonComponents/TableCardFrame';

const COLUMNS = ['Name', 'Email', 'Role'];

/**
 * The GoTu Admin list, in the shared `TableCardFrame`, which owns the loading,
 * empty and error states so the frame stays put between pages.
 *
 * No card title (the frame's default) is product's call: the page header
 * already reads "GoTu Admin", so a second heading directly beneath it is noise.
 * The frame's doc says why that leaves an empty band above the table; we chose
 * the band over composing the card by hand.
 *
 * "No admins match" must not stand in for a request that failed — the two look
 * identical to an admin narrowing a filter, and only one of them means "try
 * again" — which is why the frame gives the error its own branch.
 *
 * Read-only by design (GOTU-5861). Roles are sourced from Google Workspace and
 * auth-service does not register its PATCH route in production, so there is
 * nothing to edit here even if the page offered it.
 */
export const GotuAdminsTable = ({
  admins, isPending, isError, hasFilters, footer,
}) => (
  <TableCardFrame
    columns={COLUMNS}
    rowCount={admins.length}
    isPending={isPending}
    isError={isError}
    emptyMessage={
      hasFilters ? 'No GoTu Admins match these filters.' : 'No GoTu Admins yet.'
    }
    errorMessage="Could not load GoTu Admins."
    footer={footer}
  >
    {admins.map((admin) => (
      <TableRow key={admin.id} hover>
        <TableCell>{admin.name}</TableCell>
        <TableCell>{admin.email}</TableCell>
        <TableCell>{admin.roleLabel}</TableCell>
      </TableRow>
    ))}
  </TableCardFrame>
);

GotuAdminsTable.propTypes = {
  admins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      roleLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  isPending: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  /** Picks the empty copy: "nothing matched" reads as a bug with no filters set. */
  hasFilters: PropTypes.bool,
  /** Footer slot — the page passes its pagination. */
  footer: PropTypes.node,
};

GotuAdminsTable.defaultProps = { hasFilters: false, footer: null };

export default GotuAdminsTable;
